import { MAKE_PACKAGE_IDS, PART_NUMBER_KINDS, PART_ORIGINS } from "@bt/parts-core";
import { z } from "zod";
import { CHASSIS_CODE_COLORS } from "../chassis-codes";
import {
  DRIVE_TYPES,
  MILEAGE_UNITS,
  OWNER_TYPES,
  SUPPLIER_KINDS,
  WORK_ORDER_STATUSES,
  WORK_ORDER_TYPES,
} from "../enums";
import { languageSchema } from "../languages";
import { makePackagesOf } from "../make-packages";
import { moduleSwitchesInput } from "../modules";
import { ROADWORTHINESS_DUE_AS } from "../roadworthiness";
import { isValidTaxRates, MAX_TAX_RATE_BP, sortTaxRates } from "../tax";
import {
  currency,
  id,
  isoDate,
  longText,
  nonNegativeQty,
  nullableId,
  nullableMoneyMinor,
  optionalEmail,
  optionalIsoDate,
  optionalText,
  optionalUrl,
  positiveQty,
  qty,
  requiredText,
  unit,
  webUrl,
  yearMonth,
} from "./common";

// Inventory

export const locationInput = z.object({
  name: requiredText,
  parentId: nullableId,
  description: optionalText,
});

export const supplierInput = z.object({
  name: requiredText,
  kind: z.enum(SUPPLIER_KINDS).default("online"),
  website: optionalUrl,
  /** Where parts lists are emailed to, e.g. a dealer's parts service. */
  email: optionalEmail,
  notes: longText,
});

export const tagInput = z.object({
  name: requiredText,
  color: optionalText,
});

export const chassisCodeInput = z.object({
  name: requiredText,
  /** Null keeps the code's default colour (defaultChassisCodeColor). */
  color: z
    .enum(CHASSIS_CODE_COLORS)
    .nullish()
    .transform((v) => v ?? null),
});

const chassisCodeIds = z.array(id).max(100).default([]);

export const partNumberInput = z.object({
  number: z
    .string()
    .trim()
    .min(1, { error: "validation.required" })
    .max(100, { error: "validation.tooLong" }),
  kind: z.enum(PART_NUMBER_KINDS),
  brand: optionalText,
});

export const partInput = z.object({
  name: requiredText,
  description: longText,
  manufacturer: optionalText,
  /** Genuine, OE or aftermarket; unset when unknown or it doesn't apply (tools, oil). */
  origin: z
    .enum(PART_ORIGINS)
    .nullish()
    .transform((v) => v ?? null),
  unit,
  minStock: nonNegativeQty.nullish().transform((v) => v ?? null),
  locationId: nullableId,
  locationDetail: optionalText,
  notes: longText,
  numbers: z.array(partNumberInput).max(50),
  tagIds: z.array(id).max(50),
  chassisCodeIds,
});

export const createPartInput = partInput.extend({
  initialQty: nonNegativeQty.default(0),
});

export const partSourceInput = z.object({
  supplierId: nullableId,
  url: optionalUrl,
  supplierPartNumber: optionalText,
  priceMinor: nullableMoneyMinor,
  currency,
  isPreferred: z.boolean(),
});

export const adjustStockInput = z.object({
  partId: id,
  delta: qty.refine((v) => v !== 0, { error: "validation.nonZero" }),
  note: optionalText,
});

export const setStockInput = z.object({
  partId: id,
  qty,
  note: optionalText,
});

// Vehicles and work

export const vehicleInput = z.object({
  name: requiredText,
  make: optionalText,
  model: optionalText,
  variant: optionalText,
  modelYear: z
    .int({ error: "validation.number" })
    .min(1885, { error: "validation.year" })
    .max(2100, { error: "validation.year" })
    .nullish()
    .transform((v) => v ?? null),
  productionDate: yearMonth,
  vin: optionalText.transform((v) => (v ? v.toUpperCase().replace(/\s+/g, "") : null)),
  chassisCodeId: nullableId,
  engineCode: optionalText,
  transmission: optionalText,
  driveType: z
    .enum(DRIVE_TYPES)
    .nullish()
    .transform((v) => v ?? null),
  paintCode: optionalText,
  licensePlate: optionalText,
  /** When the next roadworthiness inspection is due; in month mode the month's first day. */
  roadworthinessDueOn: optionalIsoDate,
  mileage: z
    .int({ error: "validation.number" })
    .nonnegative({ error: "validation.nonNegative" })
    .nullish()
    .transform((v) => v ?? null),
  mileageUnit: z.enum(MILEAGE_UNITS),
  description: longText,
});

export const workOrderInput = z.object({
  vehicleId: id,
  title: requiredText,
  type: z.enum(WORK_ORDER_TYPES),
  status: z.enum(WORK_ORDER_STATUSES).exclude(["completed"]),
  plannedOn: optionalIsoDate,
  description: longText,
});

export const workOrderUpdateInput = workOrderInput.omit({ status: true });

export const requirementInput = z.object({
  partId: id,
  qty: positiveQty,
  consumesStock: z.boolean(),
  note: optionalText,
});

export const completeWorkOrderInput = z.object({
  id,
  completedOn: isoDate,
  mileage: z
    .int({ error: "validation.number" })
    .nonnegative({ error: "validation.nonNegative" })
    .nullish()
    .transform((v) => v ?? null),
  lines: z.array(z.object({ requirementId: id, qtyUsed: nonNegativeQty })),
  /** A passed roadworthiness inspection: when the vehicle's next one is due. */
  nextRoadworthinessDueOn: optionalIsoDate,
});

/** A service interval's period (months) or distance: a whole number above 0, or empty. */
const intervalAmount = (max: number) =>
  z
    .int({ error: "validation.number" })
    .positive({ error: "validation.positive" })
    .max(max, { error: "validation.tooLarge" })
    .nullish()
    .transform((v) => v ?? null);

export const templateInput = z.object({
  title: requiredText,
  type: z.enum(WORK_ORDER_TYPES),
  description: longText,
  chassisCodeIds,
  intervalMonths: intervalAmount(600),
  intervalDistance: intervalAmount(1_000_000),
  intervalDistanceUnit: z
    .enum(MILEAGE_UNITS)
    .nullish()
    .transform((v) => v ?? null),
});

export const linkInput = z.object({
  ownerType: z.enum(OWNER_TYPES),
  ownerId: id,
  title: optionalText,
  url: webUrl,
});

// Ordering

export const orderInput = z.object({
  name: requiredText,
  supplierId: nullableId,
  orderNumber: optionalText,
  orderedOn: optionalIsoDate,
  expectedOn: optionalIsoDate,
  trackingUrl: optionalUrl,
  shippingMinor: nullableMoneyMinor,
  currency,
  notes: longText,
});

export const orderLineInput = z.object({
  partId: id,
  qtyOrdered: positiveQty,
  unitPriceMinor: nullableMoneyMinor,
  workOrderId: nullableId,
  note: optionalText,
});

export const receiveInput = z.object({
  orderId: id,
  receivedOn: optionalIsoDate,
  lines: z.array(z.object({ lineId: id, qty: positiveQty })).min(1),
});

export const addToDraftInput = z.object({
  /**
   * Each item goes to its own supplier (`null`: no supplier); without `supplierId`, to its
   * preferred source's supplier.
   */
  items: z
    .array(
      z.object({
        partId: id,
        qty: positiveQty,
        workOrderId: nullableId,
        supplierId: id.nullable().optional(),
      }),
    )
    .min(1),
  /** Suppliers (`null`: no supplier) whose items start a new order instead of joining their newest one not placed yet. */
  separate: z.array(id.nullable()).max(200).default([]),
  /** Name for a new order without supplier, in the user's language. */
  draftName: optionalText,
});

/** The parts a parts list shows, with the amounts and work orders they come from. */
export const partsListInput = z.object({
  items: z
    .array(z.object({ partId: id, qty: positiveQty, workOrderIds: z.array(id).max(50) }))
    .min(1)
    .max(200),
  /** The dealer the list is for, shown in its header and used as the email recipient. */
  dealerId: nullableId,
  /** For a dealer who sells genuine parts only: alternatives become their genuine part. */
  genuineOnly: z.boolean().default(false),
});

export const quickPurchaseInput = z.object({
  supplierId: nullableId,
  name: optionalText,
  purchasedOn: isoDate,
  currency,
  lines: z
    .array(z.object({ partId: id, qty: positiveQty, unitPriceMinor: nullableMoneyMinor }))
    .min(1),
});

// Settings

/** A garage's tax rates: one base rate and dated changes (ADR 0009). */
export const taxRates = z
  .array(
    z.object({
      validFrom: isoDate.nullable(),
      rateBp: z.int({ error: "validation.number" }).min(0).max(MAX_TAX_RATE_BP),
    }),
  )
  .refine(isValidTaxRates, { error: "validation.taxRates" })
  .transform(sortTaxRates);

/** A garage's own settings, changed by its garage admins (architecture.md › Garage settings). */
export const settingsInput = z.object({
  /** The language everyone in the garage sees the app in. */
  language: languageSchema.optional(),
  currency: currency.optional(),
  mileageUnit: z.enum(MILEAGE_UNITS).optional(),
  /** Whether prices are entered and shown with tax; they are stored without (ADR 0009). */
  pricesIncludeTax: z.boolean().optional(),
  taxRates: taxRates.optional(),
  /** Whether a roadworthiness inspection's due date is entered and shown as a month or a day. */
  roadworthinessDueAs: z.enum(ROADWORTHINESS_DUE_AS).optional(),
  /** How many months a passed roadworthiness inspection lasts, to suggest the next due date. */
  roadworthinessIntervalMonths: z
    .int({ error: "validation.number" })
    .positive({ error: "validation.positive" })
    .max(120, { error: "validation.tooLarge" })
    .optional(),
  /** The make packages whose main groups and chassis codes are offered (ADR 0014). */
  makePackages: z.array(z.enum(MAKE_PACKAGE_IDS)).max(20).transform(makePackagesOf).optional(),
  /** Modules switched on or off (ADR 0017); the ones not named keep their state. */
  modules: moduleSwitchesInput.optional(),
});

/** Backups cover the whole app (all garages), so their settings are app-wide. */
export const backupSettingsInput = z.object({
  autoBackup: z.boolean().optional(),
  backupDir: z.string().trim().min(1).nullish(),
  backupKeep: z.int().min(1).max(365).optional(),
});

/** Typed as the user likes; the server normalizes it (normalizeGarageName). */
export const garageNameInput = z.object({ name: z.string().max(200) });

export type LocationInput = z.input<typeof locationInput>;
export type SupplierInput = z.input<typeof supplierInput>;
export type TagInput = z.input<typeof tagInput>;
export type ChassisCodeInput = z.input<typeof chassisCodeInput>;
export type PartInput = z.input<typeof partInput>;
export type CreatePartInput = z.input<typeof createPartInput>;
export type PartSourceInput = z.input<typeof partSourceInput>;
export type VehicleInput = z.input<typeof vehicleInput>;
export type WorkOrderInput = z.input<typeof workOrderInput>;
export type WorkOrderUpdateInput = z.input<typeof workOrderUpdateInput>;
export type RequirementInput = z.input<typeof requirementInput>;
export type TemplateInput = z.input<typeof templateInput>;
export type OrderInput = z.input<typeof orderInput>;
export type OrderLineInput = z.input<typeof orderLineInput>;
export type SettingsInput = z.input<typeof settingsInput>;
export type BackupSettingsInput = z.input<typeof backupSettingsInput>;
